import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { MessageSquare, CheckCircle, AlertCircle, User, Send } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function ClientQA({ client, getHeaders }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [answeringIdx, setAnsweringIdx] = useState(null);
  const [answerText, setAnswerText] = useState('');
  const [sending, setSending] = useState(false);

  const fetchData = () => {
    fetch(`${API}/api/client/dashboard/questions`, { headers: getHeaders() })
      .then(r => r.json())
      .then(d => setData(d))
      .catch(console.error)
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchData(); }, [getHeaders]);

  const handleAnswer = async (idx) => {
    if (!answerText.trim()) return;
    setSending(true);
    try {
      const res = await fetch(`${API}/api/client/dashboard/questions/${idx}/answer`, {
        method: 'POST',
        headers: { ...getHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ answer: answerText }),
      });
      if (res.ok) {
        setAnsweringIdx(null);
        setAnswerText('');
        fetchData();
      }
    } catch (e) { console.error(e); }
    finally { setSending(false); }
  };

  if (loading) return <div className="text-gray-400 animate-pulse py-12 text-center">Loading Q&A...</div>;
  if (!data) return <div className="text-red-400 text-center py-12">Failed to load Q&A</div>;

  return (
    <div className="space-y-6" data-testid="qa-tab">
      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Card className="bg-white border-gray-200">
          <CardContent className="pt-5 flex items-center gap-4">
            <div className="p-3 rounded-xl bg-blue-900/30 text-blue-400">
              <MessageSquare className="h-6 w-6" />
            </div>
            <div>
              <p className="text-xs text-gray-500 uppercase">Total Questions</p>
              <p className="text-2xl font-bold text-gray-900">{data.total}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white border-gray-200">
          <CardContent className="pt-5 flex items-center gap-4">
            <div className={`p-3 rounded-xl ${data.unanswered > 0 ? 'bg-amber-900/30 text-amber-400' : 'bg-green-50 text-green-400'}`}>
              {data.unanswered > 0 ? <AlertCircle className="h-6 w-6" /> : <CheckCircle className="h-6 w-6" />}
            </div>
            <div>
              <p className="text-xs text-gray-500 uppercase">Unanswered</p>
              <p className="text-2xl font-bold text-gray-900">{data.unanswered}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Questions */}
      <div className="space-y-3">
        {data.questions.length === 0 && <p className="text-gray-500 text-center py-8">No questions yet</p>}
        {data.questions.map((q, i) => (
          <Card key={i} className={`bg-white border-gray-200 ${!q.answer ? 'border-l-2 border-l-amber-500' : ''}`}>
            <CardContent className="pt-4">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-gray-800 flex items-center justify-center flex-shrink-0">
                  <User className="h-4 w-4 text-gray-400" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-sm font-medium text-gray-900">{q.asked_by || 'Anonymous'}</span>
                    <Badge className="bg-gray-800 text-gray-400 text-[10px]">{q.source || 'Google'}</Badge>
                    {!q.answer && <Badge className="bg-amber-900/30 text-amber-400 text-[10px]">Needs Response</Badge>}
                  </div>
                  <p className="text-sm text-gray-300">Q: {q.question}</p>
                  {q.answer ? (
                    <div className="mt-2 pl-3 border-l-2 border-green-600/40">
                      <p className="text-sm text-gray-400">A: {q.answer}</p>
                    </div>
                  ) : answeringIdx === i ? (
                    <div className="mt-2 space-y-2">
                      <textarea
                        value={answerText}
                        onChange={e => setAnswerText(e.target.value)}
                        placeholder="Write your answer..."
                        className="w-full bg-gray-50 border border-gray-200 text-gray-900 rounded-lg px-3 py-2 text-sm resize-none h-20 focus:border-blue-500 focus:outline-none"
                        data-testid={`qa-answer-input-${i}`}
                      />
                      <div className="flex gap-2">
                        <Button size="sm" onClick={() => handleAnswer(i)} disabled={sending} className="bg-blue-600 hover:bg-blue-700 text-xs" data-testid={`qa-answer-submit-${i}`}>
                          <Send className="h-3 w-3 mr-1" /> {sending ? 'Sending...' : 'Submit Answer'}
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => { setAnsweringIdx(null); setAnswerText(''); }} className="text-gray-400 text-xs">Cancel</Button>
                      </div>
                    </div>
                  ) : (
                    <Button size="sm" variant="ghost" onClick={() => setAnsweringIdx(i)} className="mt-1 text-blue-400 hover:text-blue-300 text-xs p-0 h-auto" data-testid={`qa-answer-btn-${i}`}>
                      Answer this question
                    </Button>
                  )}
                  <p className="text-[10px] text-gray-600 mt-2">
                    {q.asked_at ? new Date(q.asked_at).toLocaleDateString() : ''}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
